import { z } from 'zod';
import { PersonPropsSchema, PERSON_NATURAL_KEY_FIELD } from '../base-crm/person';
import { OrganizationPropsSchema, ORGANIZATION_NATURAL_KEY_FIELD } from '../base-crm/organization';
import { ActivityPropsSchema } from '../base-crm/activity';
import { TaskPropsSchema } from '../base-crm/task';
import { DocumentPropsSchema } from '../base-crm/document';
import { CommitmentPropsSchema } from '../pevc-ir/commitment';
import { InvestmentAccountPropsSchema } from '../pevc-ir/investment-account';
import { CapitalCallPropsSchema } from '../pevc-ir/capital-call';
import { DistributionPropsSchema } from '../pevc-ir/distribution';
import { ReportPropsSchema } from '../pevc-ir/report';
import { OpportunityPropsSchema } from '../sales';
import { CampaignPropsSchema, EventPropsSchema, ListPropsSchema } from '../marketing';
import { VehiclePropsSchema, FundraisingOpportunityPropsSchema } from '../pevc-fundraising';
import { DealPropsSchema } from '../pevc-dealflow';
import { InvestmentPropsSchema, ExternalFundPropsSchema } from '../pevc-portfolio';
import { PropertyPropsSchema, MetricPropsSchema, NarrativePropsSchema } from '../pmn';

/**
 * Central registry mapping node type to Zod schema
 */
export const NodeSchemaRegistry: Record<string, z.ZodType<any>> = {
  // Base CRM
  Person: PersonPropsSchema,
  Organization: OrganizationPropsSchema,
  Activity: ActivityPropsSchema,
  Task: TaskPropsSchema,
  Document: DocumentPropsSchema,

  // Sales
  Opportunity: OpportunityPropsSchema,

  // Marketing
  Campaign: CampaignPropsSchema,
  Event: EventPropsSchema,
  List: ListPropsSchema,

  // PE/VC Fundraising
  Vehicle: VehiclePropsSchema,
  FundraisingOpportunity: FundraisingOpportunityPropsSchema,

  // PE/VC IR
  Commitment: CommitmentPropsSchema,
  InvestmentAccount: InvestmentAccountPropsSchema,
  CapitalCall: CapitalCallPropsSchema,
  Distribution: DistributionPropsSchema,
  Report: ReportPropsSchema,

  // PE/VC Deal Flow
  Deal: DealPropsSchema,

  // PE/VC Portfolio
  Investment: InvestmentPropsSchema,
  ExternalFund: ExternalFundPropsSchema,

  // PMN
  Property: PropertyPropsSchema,
  Metric: MetricPropsSchema,
  Narrative: NarrativePropsSchema,
};

/**
 * Natural key field per node type.
 * Used to auto-populate natural_key from props for deduplication.
 * Only node types with a meaningful unique identifier are included.
 */
const NaturalKeyFieldMap: Record<string, string> = {
  Person: PERSON_NATURAL_KEY_FIELD,
  Organization: ORGANIZATION_NATURAL_KEY_FIELD,
};

/**
 * Get schema for a node type
 */
export function getNodeSchema(nodeType: string): z.ZodType<any> | undefined {
  return NodeSchemaRegistry[nodeType];
}

/**
 * Check if node type is valid
 */
export function isValidNodeType(nodeType: string): boolean {
  return nodeType in NodeSchemaRegistry;
}

/**
 * Get all valid node types
 */
export function getAllNodeTypes(): string[] {
  return Object.keys(NodeSchemaRegistry);
}

/**
 * Normalize a website URL for consistent deduplication.
 * Strips protocol (http://, https://) and www. prefix.
 * Returns lowercase domain.
 */
export function normalizeWebsite(url: string): string {
  return url
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, ''); // Remove trailing slash
}

/**
 * Extract natural key from props for a given node type.
 * Returns undefined if the node type has no natural key field or the value is empty.
 * Applies normalization for specific fields (e.g., website).
 */
export function extractNaturalKey(nodeType: string, props: Record<string, unknown>): string | undefined {
  const field = NaturalKeyFieldMap[nodeType];
  if (!field) return undefined;
  const value = props[field];
  if (typeof value !== 'string' || value.trim().length === 0) return undefined;

  // Normalize website for Organization
  if (field === 'website') {
    return normalizeWebsite(value);
  }

  return value.trim();
}

/**
 * Field aliases for common user language variations.
 * Maps user-friendly names to actual schema field names.
 */
const FieldAliases: Record<string, Record<string, string>> = {
  Organization: {
    location: 'geo',
    address: 'geo',
    hq: 'geo',
    headquarters: 'geo',
    url: 'website',
    site: 'website',
    type: 'organization_type',
  },
  Person: {
    location: 'geo',
    address: 'geo',
  },
};

/**
 * Normalize a field name to its canonical schema name.
 * Handles common synonyms (e.g., "location" → "geo").
 */
export function normalizeFieldName(nodeType: string, field: string): string {
  const lowerField = field.toLowerCase();
  return FieldAliases[nodeType]?.[lowerField] ?? field;
}

/**
 * Normalize all field names in a props object.
 */
export function normalizeProps(nodeType: string, props: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    const normalizedKey = normalizeFieldName(nodeType, key);
    normalized[normalizedKey] = value;
  }
  return normalized;
}
